import type { CardHooks } from "./registry";

/**
 * Ready Arms — Maneuver utility.
 * Effect: ready one of your weapons (tapped → untapped).
 *
 * Implementation: mirrors the data-driven `ready` EffectRow path that
 * `applyLoadoutEffect` runs for AD/equipment cards. Since evalMechanics
 * isn't called for utility plays, we do the same shape here from a hook:
 *   • Collect eligible (currently-tapped, occupied) own equip slot indices.
 *   • If 0 candidates → log "no weapons to ready" and bail.
 *   • If exactly 1 candidate → auto-untap it.
 *   • If 2+ candidates → surface a `pick-loadout-weapons` prompt with
 *     count = 1 so the player chooses.
 *
 * Card-form note: no new fields. utilityType is "Maneuver"; typeRequirement
 * stays where it is (currently ["Speed"]).
 */
export const readyArms: CardHooks = {
  onUtilityPlay: (ctx) => {
    const slots = ctx.self.equipSlots ?? [];
    const candidates: number[] = [];
    for (let i = 0; i < slots.length; i++) {
      const s = slots[i];
      if (s?.cardId && s.tapped) candidates.push(i);
    }
    if (candidates.length === 0) {
      return { log: ["Ready Arms — no tapped weapons to ready"] };
    }
    if (candidates.length === 1) {
      const idx = candidates[0];
      const newSlots = slots.map((s, i) =>
        i === idx ? { ...s, tapped: false } : s,
      );
      return {
        selfPatch: { equipSlots: newSlots },
        log: ["Ready Arms — readied 1 weapon"],
      };
    }
    // Multiple eligible — let the player choose.
    return {
      prompt: {
        kind: "pick-loadout-weapons",
        playerNum: ctx.self.num,
        ownerNum: ctx.self.num,
        effect: "ready",
        count: 1,
        candidateSlots: candidates,
        selectedSlots: [],
        cardName: "Ready Arms",
      },
      log: ["Ready Arms — pick 1 own weapon to ready"],
    };
  },
};
